import { combineLatest, map, Observable, tap, debounceTime } from 'rxjs';
import { WorkspaceRelationshipsViewSourceData } from '../types';
import { ViewSettingsForModelSourceData } from './viewSettingsPartitions';
import type { I18nLocale } from '@ardoq/i18n';
import {
  aggregateReferences,
  getFullComponentLabelWithLegacyFormatting,
  getGraphEdgeLabelForWorkspaceMode,
  getGraphNodeOrGroupLabelForWorkspaceMode,
  getIsShowReferenceTypeApplied,
  HasViewInstanceId,
  ItemLabels,
  RawGraphNode2,
  relationshipsViewDistinctReferenceKey,
} from '@ardoq/graph';
import { ArdoqId, ViewIds } from '@ardoq/api-types';
import type {
  ComponentsState,
  ScenarioRelatedComponentIds,
} from 'streams/components/components$';
import { globalViewModelResetSource$ } from 'tabview/relationshipDiagrams/shared/sharedGlobalStreams';
import {
  action$,
  combineReducers,
  dispatchAction,
  withNamespace,
  streamReducer,
} from '@ardoq/rxbeach';
import type { ViewStatusReporter } from 'tabview/activeViewsStatus/types';
import { reportDataWorkStarted } from 'tabview/activeViewsStatus/viewStatusReporter';
import {
  createDisconnectedContextNode,
  getCandidateComponentIds,
} from './utils';
import { buildComponentRepresentationDataByComponentId } from 'streams/buildComponentRepresentationDataByComponentId';
import { workspaceAndModel$ } from 'streams/workspaceAndModel$';
import {
  getWorkspaceModeInitialState,
  getWorkspaceModeRepresentationDataGetter,
} from './workspaceModeInitialState';
import { handleWorkspaceModeComponentsUpdate } from 'tabview/relationshipDiagrams/viewModel$/handleWorkspaceModeComponentsUpdate';
import {
  DEFAULT_EXPECTED_DURATION,
  startViewLoadTracking,
} from 'tracking/durationTracking/viewPerformance';
import { viewIdToUserFriendlyName } from 'views/metaInfoConsts';
import { getRawGraph } from 'tabview/relationshipDiagrams/viewModel$/buildViewModel';
import {
  getComponentIdsFromViewModel,
  overrideRelationshipsTraverseOptions,
} from 'tabview/relationshipDiagrams/utils';
import { componentsChanged$ } from 'streams/components/components$';
import { DEFAULT_DEBOUNCE_TIME } from 'modelInterface/consts';
import { readViewComponentIds } from 'streams/viewComponentIds$';
import { scopeDiff$ } from 'scope/scopeDiff$';
import { setStateProperty } from '@ardoq/common-helpers';
import {
  ContextShape,
  GraphModelShape,
  LoadedScenarioData,
  ScopeDiffState,
  ShowRelatedComponentsShape,
  WorkspaceAndModelLookup,
} from '@ardoq/data-model';
import { MainAppModuleSidebarState } from 'appContainer/MainAppModule/MainAppModuleSidebar/mainAppModuleSidebar$';
import {
  addScenarioRelatedComponents,
  getLabelsData,
  getPureGraphItems,
} from 'tabview/relationshipDiagrams/viewModel$/utils';
import { toViewModel } from 'tabview/relationshipDiagrams/viewModel$/viewModelFromGraph';
import { getGraphData } from 'tabview/relationshipsDiagramsViewModel/getGraphData';
import { filterInterface } from '@ardoq/filter-interface';
import { referenceInterface } from '@ardoq/reference-interface';
import { resolveEdgeReferenceDisplayLabels } from 'modelInterface/graph/resolveEdgeReferenceDisplayLabel';

const VIEW_ID = ViewIds.RELATIONSHIPS_3;

const getGraphNodeOrGroupLabel = (item: RawGraphNode2): ItemLabels => {
  const itemLabels = getGraphNodeOrGroupLabelForWorkspaceMode(item);
  return {
    mainLabel: itemLabels.legacyLabelParts
      ? getFullComponentLabelWithLegacyFormatting(itemLabels.legacyLabelParts)
      : (itemLabels.mainLabel ?? ''),
  };
};

type WorkspaceModeRelationshipsViewSourceDataShape = {
  context: ContextShape;
  components: ComponentsState;
  scenarioRelatedComponentIds: ScenarioRelatedComponentIds;
  graphModel: GraphModelShape;
  scenario: [
    LoadedScenarioData,
    ShowRelatedComponentsShape,
    MainAppModuleSidebarState,
    ScopeDiffState,
  ];
  /** Duration-tracking session the caller started, or null to not track. */
  performanceTrackingId: string | null;
  viewSettings: ViewSettingsForModelSourceData;
  workspaceAndModel: WorkspaceAndModelLookup;
  i18nLocale: I18nLocale;
};

const resetReducer = (
  previousState: WorkspaceRelationshipsViewSourceData,
  {
    context,
    components,
    scenarioRelatedComponentIds,
    graphModel,
    scenario,
    performanceTrackingId,
    viewSettings,
    workspaceAndModel,
    i18nLocale,
  }: WorkspaceModeRelationshipsViewSourceDataShape
): WorkspaceRelationshipsViewSourceData => {
  const { viewInstanceId, viewInterface, scopeDiffState } = previousState;
  const {
    collapsedGroupIds: collapsedGroupIdsList,
    ...sourceDataViewSettings
  } = viewSettings;

  const [loadedScenarioData, scenarioRelatedComponents, { showScopeRelated }] =
    scenario;

  const { rawGraph, traversalViewModel } = getRawGraph({
    viewInterface,
    context,
    components,
    scenarioRelatedComponentIds,
    viewSettings,
    graphModel,
    loadedScenarioData,
    scenarioRelatedComponents,
    isInScopeDiffMode: Boolean(scopeDiffState.scopeDiff),
    scopeDiff: scopeDiffState.scopeDiff,
    expandedStateChangingGroupId: null,
    showScopeRelated,
    overrideTraverseOptions: overrideRelationshipsTraverseOptions,
    getDistinctReferenceKey: relationshipsViewDistinctReferenceKey,
    shouldCollapseGraph: false,
    viewId: VIEW_ID,
    i18nLocale,
  });

  const collapsedGroupIds = new Set(collapsedGroupIdsList);
  const isInScopeDiffMode = Boolean(scopeDiffState.scopeDiff);

  addScenarioRelatedComponents({
    loadedScenarioData,
    showScopeRelated,
    isInScopeDiffMode,
    scenarioRelatedComponentIds: scenarioRelatedComponents.componentIds,
    componentMap: rawGraph.componentMap,
    referenceMap: new Map(rawGraph.referenceList.map(ref => [ref.id, ref])),
    graphModel,
  });

  const referenceList = rawGraph.referenceList.map(reference => ({
    ...reference,
    uncollapsedSourceId: reference.sourceId,
    uncollapsedTargetId: reference.targetId,
  }));

  const getRepresentationData =
    getWorkspaceModeRepresentationDataGetter(viewInterface);

  const graphData = getGraphData({
    viewInterface,
    componentMap: rawGraph.componentMap,
    groupMap: rawGraph.groupMap,
    referenceList,
    getRepresentationData,
  });

  const aggregatedReferenceList = aggregateReferences({
    viewInterface,
    referenceList,
    getDistinctReferenceKey: relationshipsViewDistinctReferenceKey,
    collapsedGroupIds,
    isInScopeDiffMode,
    urlFieldValuesByReferenceId: graphData.urlFieldValuesByReferenceId,
    componentMap: rawGraph.componentMap,
    groupMap: rawGraph.groupMap,
    i18nLocale,
  });

  const isShowReferenceTypeApplied = getIsShowReferenceTypeApplied(
    viewInterface.getAllReferenceLabelFilters()
  );
  const resolvedEdgeReferenceDisplayLabels = resolveEdgeReferenceDisplayLabels(
    aggregatedReferenceList
  );
  const { nodeLabelsData, edgeLabelsData } = getLabelsData({
    componentMap: rawGraph.componentMap,
    groupMap: rawGraph.groupMap,
    rawReferences: aggregatedReferenceList,
    getGraphNodeOrGroupLabel,
    i18nLocale,
    getGraphEdgeLabel: (item, isShowReferenceTypeApplied) =>
      getGraphEdgeLabelForWorkspaceMode(
        item,
        resolvedEdgeReferenceDisplayLabels.get(item.id) ?? null,
        i18nLocale,
        isShowReferenceTypeApplied
      ),
    isShowReferenceTypeApplied,
  });

  const { graphNodes, groupNodes, edges } = getPureGraphItems({
    i18nLocale,
    viewInterface,
    groupMap: rawGraph.groupMap,
    componentMap: rawGraph.componentMap,
    rawReferences: aggregatedReferenceList,
    componentIdsOfCollapsedGroups: [],
    rawCollapsedGroupIds: collapsedGroupIds,
    scopeDiff: scopeDiffState.scopeDiff,
    contextComponentId: context.componentId,
    nodeLabelsData,
    edgeLabelsData,
    ...graphData,
  });

  const noConnectedComponents =
    viewSettings.showOnlyConnectedComponents &&
    graphNodes.size + groupNodes.size === 0;

  const contextNodeReplacement =
    noConnectedComponents && context.componentId
      ? createDisconnectedContextNode({
          viewInterface,
          componentId: context.componentId,
          getRepresentationData,
        })
      : null;

  const formattingFilters = filterInterface.getFormattingFilters();

  const colorFilterIdsByComponentId = new Map<ArdoqId, string[]>();
  for (const rawItem of rawGraph.componentMap.values()) {
    if (!rawItem.modelId) continue;
    const ids = filterInterface.getColorFiltersForComponent(rawItem.modelId);
    if (ids?.length) {
      colorFilterIdsByComponentId.set(rawItem.modelId, ids);
    }
  }

  const colorFilterIdsByReferenceId = new Map<ArdoqId, string[]>();
  for (const referenceRawGraphItem of rawGraph.referenceList) {
    if (!referenceRawGraphItem.modelId) continue;
    const filterIds = referenceInterface.getMatchingColorFilterIds(
      referenceRawGraphItem.modelId
    );
    if (filterIds?.length) {
      colorFilterIdsByReferenceId.set(referenceRawGraphItem.modelId, filterIds);
    }
  }

  const nodes = contextNodeReplacement
    ? toViewModel([contextNodeReplacement])
    : toViewModel([...graphNodes.values()]);
  const groups = toViewModel([...groupNodes.values()]);
  const componentRepresentationDataByComponentId =
    buildComponentRepresentationDataByComponentId(
      getCandidateComponentIds({ nodes, groups }),
      components.byId,
      workspaceAndModel
    );

  return {
    ...previousState,
    ...graphData,
    ...traversalViewModel,
    i18nLocale,
    quickPerspectiveReferenceTypeFilters:
      filterInterface.getActiveQuickReferenceTypeFilters(),
    nodes,
    edges: toViewModel(edges),
    groups,
    componentRepresentationDataByComponentId,
    errors: rawGraph.errors,
    hasClones: rawGraph.hasClones,
    expandedStateChangingGroupId: null,
    componentIdsOfCollapsedGroups: [],
    nodeLabelsData,
    edgeLabelsData,
    getRepresentationData,
    viewInterface,
    rawGraph,
    viewSettings: sourceDataViewSettings,
    context,
    graphModel,
    loadedScenarioData,
    scenarioRelatedComponents,
    showScopeRelated,
    performanceTrackingId,
    viewInstanceId,
    scopeDiffState,
    noContext: false,
    noConnectedComponents,
    isViewpointMode: false,
    users: previousState.users,
    formattingFilters,
    colorFilterIdsByComponentId,
    colorFilterIdsByReferenceId,
  };
};

const startTracking = (
  args: WorkspaceModeRelationshipsViewSourceDataShape
): WorkspaceModeRelationshipsViewSourceDataShape => ({
  ...args,
  performanceTrackingId: startViewLoadTracking(
    viewIdToUserFriendlyName[VIEW_ID],
    DEFAULT_EXPECTED_DURATION
  ),
});

export type GetWorkspaceModeSourceDataArgs = HasViewInstanceId & {
  viewSettingsForModelSourceData$: Observable<ViewSettingsForModelSourceData>;
  viewStatusReporter: ViewStatusReporter;
};
export const getWorkspaceModeSourceData$ = ({
  viewInstanceId,
  viewSettingsForModelSourceData$,
  viewStatusReporter,
}: GetWorkspaceModeSourceDataArgs): Observable<WorkspaceRelationshipsViewSourceData> => {
  const reset$: Observable<WorkspaceModeRelationshipsViewSourceDataShape> =
    combineLatest([
      globalViewModelResetSource$,
      viewSettingsForModelSourceData$,
      workspaceAndModel$,
    ]).pipe(
      map(([globalAppState, viewSettings, workspaceAndModel]) => ({
        ...globalAppState,
        performanceTrackingId: null,
        viewSettings,
        workspaceAndModel,
      })),
      map(startTracking),
      reportDataWorkStarted(viewStatusReporter, 'reset')
    );

  const initialState = getWorkspaceModeInitialState(viewInstanceId);

  return action$.pipe(
    withNamespace(viewInstanceId),
    combineReducers(initialState, [
      streamReducer(reset$, resetReducer),
      streamReducer(componentsChanged$, handleWorkspaceModeComponentsUpdate),
      streamReducer(scopeDiff$, setStateProperty('scopeDiffState')),
    ]),
    debounceTime(2 * DEFAULT_DEBOUNCE_TIME),
    tap(viewModel => {
      dispatchAction(
        readViewComponentIds({
          [VIEW_ID]: getComponentIdsFromViewModel(viewModel),
        })
      );
    })
  );
};
